/* eslint-disable no-useless-escape */
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import {
  generatePardotTrackingValues,
  recordFormSubmit,
} from './analytics-helper';

export default class FormHandler extends Component {
  constructor(props) {
    super(props);
    const { formFields } = this.props.formHandlerConfigObj || {};
    const formFieldsValues = {};
    formFields.forEach(({ name }) => {
      formFieldsValues[name] = '';
    });
    this.state = {
      formFieldsValues,
      urlPath: '',
      errors: {},
      errormessage: '',
      formStatus: undefined,
    };
  }

  handleInputChange = (e, checkboxValue) => {
    const { name, value, type, checked } = e.target;
    let updatedValue = value;
    // If the input type is checkbox, update the value using 'checked' property
    if (type === 'checkbox') {
      updatedValue = checked ? checkboxValue : '';
    }

    const { formFieldsValues, errors } = this.state;
    // Remove the error for the current field
    delete errors[name];
    this.setState({
      formFieldsValues: {
        ...formFieldsValues, // Copy the existing formFields
        [name]: updatedValue,
      },
      errors,
    });
  };

  getFieldConfig = (fieldName) => {
    return this.props.formHandlerConfigObj.formFields.find(
      (field) => field.name === fieldName,
    );
  };

  validateForm = (formData) => {
    let fieldErrors = {};

    const applyValidation = (
      fieldName,
      fieldValue,
      validations,
      isRequired,
    ) => {
      if (!fieldErrors[fieldName]) {
        fieldErrors[fieldName] = [];
      }

      const handleInputTypeValidation = (
        fieldValue,
        fieldName,
        validation,
        fieldErrors,
      ) => {
        if (fieldValue) {
          switch (validation.inputType) {
            case 'business-email':
              if (
                !fieldValue.match(
                  /^((?!(@yahoo\.com|outlook\.com|@gmail\.com|hotmail\.com|qq\.com)).)*$/g,
                )
              ) {
                fieldErrors[fieldName].push(validation.errorMessage);
              }
              break;

            case 'email':
              if (
                !fieldValue.match(
                  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([ÀÈÌÒÙàèìòùÁÉÍÓÚÝáéíóúýÂÊÎÔÛâêîôûÃÑÕãñõÄËÏÖÜäëïöü¡¿çÇßØøÅåÆæÞþÐð""\D\s-'.,&amp;#@:?!()$\/]+\.)+[a-zA-Z]{2,}))$/,
                )
              ) {
                fieldErrors[fieldName].push(validation.errorMessage);
              }
              break;

            case 'name':
              if (
                !fieldValue.match(
                  /^[ÀÈÌÒÙàèìòùÁÉÍÓÚÝáéíóúýÂÊÎÔÛâêîôûÃÑÕãñõÄËÏÖÜäëïöü¡¿çÇßØøÅåÆæÞþÐð""\D\s-'.,&amp;#@:?!()$\/]+(\s{0,1}[ÀÈÌÒÙàèìòùÁÉÍÓÚÝáéíóúýÂÊÎÔÛâêîôûÃÑÕãñõÄËÏÖÜäëïöü¡¿çÇßØøÅåÆæÞþÐð""\D\s-'.,&amp;#@:?!()$\/])*$/,
                )
              ) {
                fieldErrors[fieldName].push(validation.errorMessage);
              }
              break;

            case 'min-length':
              if (fieldValue.length > validation.value) {
                fieldErrors[fieldName].push(validation.errorMessage);
              }
              break;

            case 'phone-number':
              if (!fieldValue.match(/^([0-9]{6,25})$/)) {
                fieldErrors[fieldName].push(validation.errorMessage);
              }
              break;

            default:
              break;
          }
        }
      };

      validations.forEach((validation) => {
        switch (validation.rule) {
          case 'required':
            if (!fieldValue && isRequired) {
              fieldErrors[fieldName].push(validation.errorMessage);
            }
            break;

          case 'input-type':
            handleInputTypeValidation(
              fieldValue,
              fieldName,
              validation,
              fieldErrors,
            );
            break;

          default:
            break;
        }
      });
    };

    Object.entries(formData).forEach(([fieldName, fieldValue]) => {
      const field = this.getFieldConfig(fieldName);
      field?.validations &&
        applyValidation(
          field.name,
          fieldValue,
          field.validations,
          field.required,
        );
    });

    return fieldErrors;
  };

  isFormValid = (errors) => {
    const isValid = Object.values(errors).every(
      (value) => Array.isArray(value) && value.length === 0,
    );
    if (!isValid) {
      this.setState({ errors });
    }
    return isValid;
  };

  handleFormError = (errors, recordSubmitParams) => {
    const errorMessages = Object.keys(errors).reduce(
      (acc, key) => [...acc, ...errors[key]],
      [],
    );
    recordSubmitParams.reason = errorMessages;
    recordFormSubmit(recordSubmitParams);
  };

  handleSubmit = (e) => {
    e.preventDefault();

    const { formFieldsValues } = this.state;

    const errors = this.validateForm(formFieldsValues);

    const {
      actionUrl,
      formSubmissionCrticalErrorMessage,
      formName,
      redirectUrl,
    } = this.props.formHandlerConfigObj;

    const appEntryPoint = 'brc/';
    const pageURI = `article/${this.props.slugId}`;
    const recordSubmitParams = {
      formType: formName,
      isSuccess: false,
      reason: '',
      pageURI,
      countryCode: this.props.countryCode,
      userType: this.props.userType,
      userLoggedIn: '',
      userAccountNumber: this.props.encryptedAccountNumber,
      appEntryPoint,
    };

    if (!this.isFormValid(errors)) {
      this.handleFormError(errors, recordSubmitParams);
      return; // Prevent form submission if there are errors
    }

    const formData = this.state.formFieldsValues;
    const body = document.getElementsByTagName('body')[0];
    const csrfToken = body.getAttribute('data-token');
    const pardotTrackingValues = generatePardotTrackingValues({
      actionUrl,
      csrfToken,
    });
    const formDataWithTrackingValues = {
      ...formData,
      ...pardotTrackingValues,
    };

    const url = `/${this.props.countryCode.toLowerCase()}/brc/api/pardot/save-form-data`;

    this.setState({
      formStatus: 'submit-in-progress',
      errormessage: '',
    });
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      redirect: 'error',
      body: new URLSearchParams(formDataWithTrackingValues),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('something went wrong');
        }
      })
      .then(() => {
        this.setState({
          formStatus: 'success',
          errormessage: '',
        });
        window.location.assign(
          `/${this.props.countryCode?.toLowerCase() || ''}/brc${redirectUrl}`,
        );
        recordSubmitParams.isSuccess = true;
        recordFormSubmit(recordSubmitParams);
      })
      .catch(() => {
        this.setState({
          formStatus: 'error',
          errormessage: formSubmissionCrticalErrorMessage.errorMessage,
        });
      });
  };

  renderDropdownField = (field) => {
    const { label, name, placeholder, required, options, span } = field;
    const { errors } = this.state;
    const isError = errors[field.name];

    return (
      <div
        className={`ppvx_col-sm-12 ${
          span === 6 ? 'ppvx_col-md-6' : 'ppvx_col-md-12'
        }`}
      >
        <div className="pd-select ppvx_dropdown">
          <select
            name={name}
            required={required}
            onChange={this.handleInputChange}
            className={`select ppvx_dropdown__control ${
              isError && isError.length > 0 ? 'error' : ''
            }`}
          >
            <option value="" selected>
              {placeholder}
            </option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <label className="ppvx_text-input__label">{label}</label>
          {isError && isError.length > 0 && (
            <div className="ppvx_text-input__error-text">{isError}</div>
          )}
        </div>
      </div>
    );
  };

  renderNumber(field) {
    return (
      <div
        className={`ppvx_col-sm-12 ${
          field.span === 6 ? 'ppvx_col-md-6' : 'ppvx_col-md-12'
        }`}
      >
        <input
          key={field.name}
          type="number"
          id={field.name}
          name={field.name}
          value={this.state.formFieldsValues[field.name]}
          onChange={this.handleInputChange}
          required={field.required}
          className={`text form-control vx_form-control ${field.name}`}
        />
        <label className="ppvx_text-input__label">{field.label}</label>
      </div>
    );
  }

  renderText(field) {
    const { errors } = this.state;
    const isError = errors[field.name];
    return (
      <div
        className={`ppvx_col-sm-12 ${
          field.span === 6 ? 'ppvx_col-md-6' : 'ppvx_col-md-12'
        }`}
      >
        <div
          className={`ppvx_text-input pd-text ${
            isError && isError.length > 0 ? 'error' : ''
          }`}
        >
          <input
            key={field.name}
            type="text"
            id={field.name}
            name={field.name}
            value={this.state.formFieldsValues[field.name]}
            onChange={this.handleInputChange}
            required={field.required}
            className={'text ppvx_text-input__control'}
            maxLength={field.maxLength}
          />
          <label className="ppvx_text-input__label">{field.label}</label>
        </div>

        {isError && isError.length > 0 && (
          <div className="ppvx_text-input__error-text">{isError}</div>
        )}
      </div>
    );
  }

  renderEmail(field) {
    const { errors } = this.state;
    const isError = errors[field.name];
    return (
      <div
        className={`ppvx_col-sm-12 ${
          field.span === 6 ? 'ppvx_col-md-6' : 'ppvx_col-md-12'
        }`}
      >
        <div
          className={`ppvx_text-input  pd-text ${
            isError && isError.length > 0 ? 'error' : ''
          }`}
        >
          <input
            key={field.name}
            type="email"
            id={field.name}
            name={field.name}
            value={this.state.formFieldsValues[field.name]}
            onChange={this.handleInputChange}
            required={field.required}
            className="text ppvx_text-input__control"
          />
          <label className="ppvx_text-input__label">{field.label}</label>
        </div>
        {isError && isError.length > 0 && (
          <div className="ppvx_text-input__error-text">{isError}</div>
        )}
      </div>
    );
  }

  handleCheckboxChange = (e) => {
    this.setState({
      isChecked: e.target.checked,
    });
  };
  renderCheckbox = ({
    label,
    supportingText,
    name,
    checked,
    required,
    span,
  }) => {
    const { errors } = this.state;
    const isError = errors[name];
    const value = label + supportingText;
    return (
      <div
        className={`ppvx_col-sm-12 ${
          span === 6 ? 'ppvx_col-md-6' : 'ppvx_col-md-12'
        }`}
      >
        <div
          className={`full-width pd-checkbox required no-label ${
            isError && isError.length > 0 ? 'error' : ''
          }`}
        >
          <div className="ppvx_checkbox_group">
            <div className="ppvx_checkbox">
              <input
                type="checkbox"
                id={name}
                name={name}
                checked={checked}
                required={required}
                onChange={(e) => this.handleInputChange(e, value)}
              />
              <label className="inline ppvx_checkbox__label" htmlFor={name}>
                <div dangerouslySetInnerHTML={{ __html: label }} />
              </label>
            </div>
          </div>
          <label>{supportingText}</label>
        </div>
        {isError && isError.length > 0 && (
          <div className="ppvx_text-input__error-text">{isError}</div>
        )}
      </div>
    );
  };

  renderFormField(field) {
    switch (field.type) {
      case 'dropdown':
        return this.renderDropdownField(field);
      case 'text':
        return this.renderText(field);
      case 'email':
        return this.renderEmail(field);
      case 'checkbox':
        return this.renderCheckbox(field);
      default:
        return null;
    }
  }

  handleButtonClick = (e) => {
    if (this.state.formStatus === 'submit-in-progress') e?.preventDefault();
  };
  render() {
    const { formStatus, errormessage } = this.state;
    const { formFields, submitButtonText } = this.props.formHandlerConfigObj;
    return (
      <div className="formHandler">
        {formStatus === 'error' && errormessage && (
          <div className="error-message">{errormessage}</div>
        )}
        <form
          noValidate
          onSubmit={this.handleSubmit}
          method="post"
          className="ppvx_container--form"
        >
          <div className="ppvx_row">
            {formFields?.map((field) => (
              <React.Fragment key={field.name}>
                {this.renderFormField(field)}
              </React.Fragment>
            ))}
          </div>

          <p className="ppvx_table__column--center submit">
            <input
              type="submit"
              id="form-submit-btn"
              className="ppvx_btn pypl-btn btns"
              value={submitButtonText}
              data-pa-click='{"link":"pardot-form-submit-button", "event_name":"ppcom_form_submit_button_clicked"}'
              onClick={this.handleButtonClick}
            />
          </p>

          <div
            style={{
              position: 'absolute',
              left: '-9999px',
              top: '-9999px',
            }}
          >
            <label htmlFor="pardot_extra_field">Comments</label>
            <input
              type="text"
              id="pardot_extra_field"
              name="pardot_extra_field"
              aria-hidden={'true'}
            />
          </div>
        </form>
      </div>
    );
  }
}

FormHandler.propTypes = {
  className: PropTypes.string,
  countryCode: PropTypes.string,
  fpti: PropTypes.shape(),
  userAccountInfo: PropTypes.string,
  userType: PropTypes.string,
  encryptedAccountNumber: PropTypes.string,
  slugId: PropTypes.string,
  locale: PropTypes.string,
  formHandlerConfigObj: PropTypes.array,
};

FormHandler.defaultProps = {
  className: '',
  countryCode: '',
  fpti: {},
  userType: '',
  userAccountInfo: '',
  encryptedAccountNumber: '',
  slugId: '',
  locale: '',
  formHandlerConfigObj: [],
};
